import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { DISCOUNTS_ENABLED } from '../content/features';
import './ContactPage.css';

const REASONS = DISCOUNTS_ENABLED ? [
  'General Inquiry',
  'Business Partnership',
  'Technical Support',
  'Billing',
  'Other',
] : [
  'General Inquiry',
  'Report a Review or Video',
  'Business Listing',
  'Technical Support',
  'Other',
];

export default function ContactPage() {
  const [status, setStatus] = useState('idle');
  const [form, setForm] = useState({ name: '', email: '', phone: '', reason: '', message: '', website: '' });
  const [sentEmail, setSentEmail] = useState('');
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    else if (form.name.length > 100) e.name = 'Use 100 characters or fewer.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || form.email.length > 254) e.email = 'Enter a valid email.';
    if (!form.reason) e.reason = 'Please select a reason.';
    if (!form.message.trim()) e.message = 'Message is required.';
    else if (form.message.length > 5000) e.message = 'Use 5,000 characters or fewer.';
    return e;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(err => ({ ...err, [name]: '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('sending');
    try {
      await httpsCallable(functions, 'sendContactMessage')(form);
      setSentEmail(form.email);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', reason: '', message: '', website: '' });
    } catch {
      setStatus('error');
    }
  }

  return (
    <main className="contact-page">
      <div className="contact-page__inner">
        <div className="contact-page__header">
          <h1 className="contact-page__title">Get in Touch</h1>
          <p className="contact-page__sub">
            Have a question or feedback? We'd love to hear from you.
          </p>
        </div>

        <div className="contact-page__grid">
          <div className="contact-page__info">
            <div className="contact-info-item">
              <span className="contact-info-item__icon" aria-hidden="true">✉️</span>
              <div>
                <h3>Email</h3>
                <a href="mailto:contact@volcyy.com">contact@volcyy.com</a>
              </div>
            </div>
            <div className="contact-info-item">
              <span className="contact-info-item__icon" aria-hidden="true">📍</span>
              <div>
                <h3>Service Area</h3>
                <p>Worldwide</p>
              </div>
            </div>
          </div>

          <div className="contact-page__form-wrap">
            {status === 'success' ? (
              <div className="contact-page__success" role="alert">
                <span className="contact-page__success-mark" aria-hidden="true">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12.5l4.4 4.3L19 7.4" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h3>Message sent!</h3>
                <p>We'll get back to you at <strong>{sentEmail}</strong> as soon as possible.</p>
                <button onClick={() => setStatus('idle')}>Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="contact-form__honeypot" aria-hidden="true">
                  <label htmlFor="cf-website">Website</label>
                  <input id="cf-website" name="website" tabIndex="-1" autoComplete="off" value={form.website} onChange={handleChange} />
                </div>
                <div className="contact-form__field">
                  <label htmlFor="cf-name">Full Name *</label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    aria-required="true"
                    aria-describedby={errors.name ? 'cf-name-err' : undefined}
                  />
                  {errors.name && <span id="cf-name-err" className="contact-form__error" role="alert">{errors.name}</span>}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="cf-email">Email Address *</label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="John@example.com"
                    aria-required="true"
                    aria-describedby={errors.email ? 'cf-email-err' : undefined}
                  />
                  {errors.email && <span id="cf-email-err" className="contact-form__error" role="alert">{errors.email}</span>}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="cf-phone">Phone Number</label>
                  <input
                    id="cf-phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(202) 555-0100"
                  />
                </div>

                <div className="contact-form__field">
                  <label htmlFor="cf-reason">Reason for Contact *</label>
                  <select
                    id="cf-reason"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    aria-required="true"
                    aria-describedby={errors.reason ? 'cf-reason-err' : undefined}
                  >
                    <option value="">Select a reason…</option>
                    {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {errors.reason && <span id="cf-reason-err" className="contact-form__error" role="alert">{errors.reason}</span>}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="cf-message">Message *</label>
                  <textarea
                    id="cf-message"
                    name="message"
                    rows={6}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us more…"
                    aria-required="true"
                    aria-describedby={errors.message ? 'cf-message-err' : undefined}
                  />
                  {errors.message && <span id="cf-message-err" className="contact-form__error" role="alert">{errors.message}</span>}
                </div>

                {status === 'error' && (
                  <p className="contact-form__send-error" role="alert">
                    Failed to send — please email us directly at <a href="mailto:contact@volcyy.com">contact@volcyy.com</a>.
                  </p>
                )}

                <button type="submit" className="contact-form__submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
